/**
 * Landing page content that is data, not copy: which product screenshots the
 * slider shows, and which OCPP frames the hero trace plays.
 *
 * Copy lives in the translation files under `landing.*`. Each slide's title and
 * caption are looked up as `landing.slider.<id>.title` / `.caption`, so adding a
 * screenshot means adding one entry here plus the two keys in en_US/uk_UA.
 */

export type LandingSlide = {
  id: string;
  /**
   * Served from `public/landing/`. Captured from the real dashboard running on
   * a seeded demonstration network — see `scripts/seed-demo.ts` in ev-backend
   * and `tests/e2e/capture-landing.spec.ts`.
   */
  image: string;
};

export const LANDING_SLIDES: LandingSlide[] = [
  { id: 'analytics', image: '/landing/analytics.png' },
  { id: 'idleRevenue', image: '/landing/idle-revenue.png' },
  { id: 'sessions', image: '/landing/sessions.png' },
  { id: 'stations', image: '/landing/stations.png' },
  { id: 'stationControl', image: '/landing/station-control.png' },
  { id: 'ocppTerminal', image: '/landing/ocpp-terminal.png' },
  { id: 'users', image: '/landing/users.png' },
  { id: 'privacy', image: '/landing/privacy-mode.png' },
];

/**
 * Direction of an OCPP frame, from the operator's point of view: `up` is the
 * station calling the central system, `down` is the central system answering or
 * commanding.
 */
export type TraceDirection = 'up' | 'down';

export type TraceStep = {
  time: string;
  direction: TraceDirection;
  /** Real OCPP 1.6 action names — the same ones ev-backend handles. */
  action: string;
  detail: string;
  /** Marks the frame whose kWh figure counts up while the trace plays. */
  isMeter?: boolean;
};

export const OCPP_TRACE: TraceStep[] = [
  { time: '14:02:11', direction: 'up', action: 'BootNotification', detail: 'model DC-60 · fw 1.4.2' },
  { time: '14:02:11', direction: 'down', action: 'Accepted', detail: 'heartbeat 300s' },
  { time: '14:02:12', direction: 'up', action: 'StatusNotification', detail: 'connector 1 · Available' },
  { time: '14:31:44', direction: 'up', action: 'Authorize', detail: 'idTag accepted' },
  { time: '14:31:45', direction: 'down', action: 'RemoteStartTransaction', detail: 'connector 1' },
  { time: '14:31:47', direction: 'up', action: 'StartTransaction', detail: 'meterStart 41 820 Wh' },
  { time: '15:04:20', direction: 'up', action: 'MeterValues', detail: '7.4 kW · 32 A', isMeter: true },
  { time: '15:12:19', direction: 'up', action: 'StopTransaction', detail: 'reason Remote · 18.42 kWh' },
];

/** Final value of the counter animated on the MeterValues frame. */
export const TRACE_ENERGY_KWH = 18.42;
